/**
 * Info Drawer Component
 * Generic right-side slide-in drawer for displaying page content
 * Follows the same pattern as size-chart-drawer and cart-drawer
 */

class InfoDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.overlay = null;
    this.isOpen = false;
  }

  connectedCallback() {
    // Move to document.body to escape any ancestor stacking contexts
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return; // connectedCallback will fire again once appended to body
    }

    this.drawer = this.querySelector('.info-drawer');
    this.overlay = document.getElementById('overlay');

    // Close button
    const closeButton = this.drawer?.querySelector('.info-drawer__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this._onOverlayClick = () => {
        if (this.overlay?.classList.contains('info-drawer-overlay')) {
          this.close();
        }
      };
      this.overlay.addEventListener('click', this._onOverlayClick);
    }

    // ESC key
    this.drawer?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });

    // Bind triggers that reference this drawer
    const drawerId = this.id;
    if (drawerId) {
      document.querySelectorAll(`[data-drawer-trigger="${drawerId}"]`).forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });
    }
  }

  disconnectedCallback() {
    // The overlay is a shared global element; remove our listener so handlers don't accumulate
    if (this.overlay && this._onOverlayClick) {
      this.overlay.removeEventListener('click', this._onOverlayClick);
    }
  }

  open() {
    if (this.isOpen) return;

    this.classList.add('active');
    if (this.overlay) {
      this.overlay.classList.add('active', 'info-drawer-overlay');
    }

    lockBodyScroll();
    this.isOpen = true;

    // Focus close button
    requestAnimationFrame(() => {
      const closeBtn = this.drawer?.querySelector('.info-drawer__close');
      if (closeBtn) closeBtn.focus();
    });
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('active');
    if (this.overlay) {
      this.overlay.classList.remove('active');
      this.overlay.classList.add('overlay-fading');
      setTimeout(() => {
        this.overlay.classList.remove('info-drawer-overlay', 'overlay-fading');
      }, 300);
    }

    unlockBodyScroll();
    this.isOpen = false;
  }
}

if (!customElements.get('info-drawer')) {
  customElements.define('info-drawer', InfoDrawer);
}
